import { useMemo } from "react";
import { useSelector } from "react-redux";
import { MpStoreState } from "@bookingcom/mp-flights";
import usePaxWithBaggages from "../../../hooks/usePaxWithBaggages";
import { getExtraProducts } from "../../store/extraProducts/selectors";
import { getIsFlexibleTicketPreSelectedAtMeta } from "../../../store/selectors/checkoutSteps";
import useIsBrandedFare from "../../../branded-fares/hooks/useIsBrandedFare";
import useShouldShowTravelInsurance from "../../hooks/TravelInsurance/useShouldShowTravelInsurance";
import { getCartDetails } from "../../../store/cartDetails/selectors";
import useMultiProduct from "../../../hooks/useMultiProduct";
import flights_apex_web_persist_explicit_selection from "../../../utils/experiments/apex/flights_apex_web_persist_explicit_selection";
import useUserAgent from "../../../hooks/useUserAgent";
import useOnBack from "../../../hooks/useOnBack";

export type CheckoutAncillaryOffersControllerProps = {
  setMpState?: React.Dispatch<React.SetStateAction<MpStoreState>>;
};

export function useCheckoutAncillaryOffersController(props: CheckoutAncillaryOffersControllerProps) {
  const { setMpState } = props;
  const isBrandedFare = useIsBrandedFare();
  const { isPaxWithBaggages } = usePaxWithBaggages();
  const cartDetails = useSelector(getCartDetails);
  const extraProducts = useSelector(getExtraProducts);
  const isMPEnabled = useMultiProduct(cartDetails?.cart?.supplyType);
  const isFlexibleTicketPreselectedAtMeta = useSelector(getIsFlexibleTicketPreSelectedAtMeta);
  const isEligibleToShowFlexibility = useMemo(
    () => isBrandedFare && !isFlexibleTicketPreselectedAtMeta,
    [isBrandedFare, isFlexibleTicketPreselectedAtMeta]
  );
  const { isMobile } = useUserAgent();

  const fastTrack = useMemo(() => extraProducts?.fastTrack, [extraProducts]);
  const mealPreference = useMemo(() => extraProducts?.mealPreference, [extraProducts]);
  const flexibleTicket = useMemo(() => extraProducts?.flexibleTicket, [extraProducts]);
  const travelInsurance = useMemo(() => extraProducts?.travelInsurance, [extraProducts]);
  const cancelForAnyReason = useMemo(() => extraProducts?.cancelForAnyReason, [extraProducts]);
  const canRenderBaggagePerPerson = useMemo(() => !isPaxWithBaggages, [isPaxWithBaggages]);
  const canRenderMealPreference = useMemo(() => !!mealPreference, [mealPreference]);
  const canRenderFastTrackExtras = useMemo(() => !!fastTrack, [fastTrack]);
  const canRenderFlexibleTicket = useMemo(
    () => isEligibleToShowFlexibility && (!!flexibleTicket || !!cancelForAnyReason),
    [isEligibleToShowFlexibility, flexibleTicket, cancelForAnyReason]
  );
  const isInsurancePresent = useMemo(
    () => !!travelInsurance?.options && Object.keys(travelInsurance.options).length > 0,
    [travelInsurance]
  );
  const shouldShowTravelInsurance = useShouldShowTravelInsurance(isInsurancePresent, travelInsurance?.ageLimit);
  const canRenderTravelInsurance = useMemo(
    () => shouldShowTravelInsurance && !!travelInsurance,
    [travelInsurance, shouldShowTravelInsurance]
  );
  const canRenderMpTravelInsurance = useMemo(
    () => isMPEnabled && !!setMpState && !!cartDetails?.basketReference,
    [isMPEnabled, setMpState, cartDetails]
  );
  const canRenderFlexExtrasTitle = useMemo(
    () => !!cancelForAnyReason && isEligibleToShowFlexibility,
    [cancelForAnyReason, isEligibleToShowFlexibility]
  );

  /* start - flights_apex_web_persist_explicit_selection */
  const canPersistExplicitSelection = canRenderFlexExtrasTitle || canRenderFlexibleTicket || canRenderTravelInsurance;

  if (canPersistExplicitSelection) {
    flights_apex_web_persist_explicit_selection.trackStages(
      isMobile,
      !!canRenderFlexExtrasTitle,
      !!canRenderFlexibleTicket,
      !!canRenderTravelInsurance
    );
  }

  useOnBack(() => {
    if (canPersistExplicitSelection) {
      flights_apex_web_persist_explicit_selection.stages.go_back();
    }
  });
  /* end - flights_apex_web_persist_explicit_selection */

  const isEmptyAncillaryPage = useMemo(
    () =>
      !canRenderBaggagePerPerson &&
      !canRenderMealPreference &&
      !canRenderFlexibleTicket &&
      !canRenderFastTrackExtras &&
      !canRenderMpTravelInsurance &&
      !canRenderTravelInsurance,
    [
      canRenderBaggagePerPerson,
      canRenderMealPreference,
      canRenderFlexibleTicket,
      canRenderFastTrackExtras,
      canRenderMpTravelInsurance,
      canRenderTravelInsurance
    ]
  );

  return {
    canRenderBaggagePerPerson,
    canRenderMealPreference,
    canRenderFastTrackExtras,
    canRenderFlexibleTicket,
    canRenderTravelInsurance,
    canRenderMpTravelInsurance,
    canRenderFlexExtrasTitle,
    isEmptyAncillaryPage
  };
}
